/**
 * Admin API Client
 *
 * This API client is used for admin bootstrap mode, where the admin
 * authenticates with username/password instead of SSO.
 *
 * It uses admin tokens from adminAuthService instead of SSO tokens.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { adminAuthService } from './adminAuth';
import type { AppConfig, Integration } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT || '/api';

class AdminApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add admin auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = adminAuthService.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle errors - redirect to admin login on 401
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired or invalid, redirect to admin login
          adminAuthService.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  // =============================================================================
  // Configuration Endpoints
  // =============================================================================

  async getConfig(): Promise<AppConfig> {
    const response = await this.client.get('/admin/config');
    return response.data;
  }

  async updateConfig(updates: Partial<AppConfig>): Promise<AppConfig> {
    const response = await this.client.patch('/admin/config', updates);
    return response.data;
  }

  // =============================================================================
  // Integration Endpoints
  // =============================================================================

  async getIntegrations(): Promise<Integration[]> {
    const response = await this.client.get('/admin/integrations');
    return response.data;
  }

  async getIntegration(type: string): Promise<Integration> {
    const response = await this.client.get(`/admin/integrations/${type}`);
    return response.data;
  }

  async updateIntegration(type: string, config: Record<string, unknown>): Promise<Integration> {
    const response = await this.client.patch(`/admin/integrations/${type}`, config);
    return response.data;
  }

  async testIntegration(type: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post(`/admin/integrations/${type}/test`);
    return response.data;
  }

  // =============================================================================
  // SSO Configuration Endpoints
  // =============================================================================

  async getSsoStatus(): Promise<{
    active_provider: string | null;
    cognito_entity_id: string;
    cognito_acs_url: string;
    providers: Record<string, { configured: boolean; enabled: boolean }>;
  }> {
    const response = await this.client.get('/admin/sso/status');
    return response.data;
  }

  async configureMsEntra(config: {
    tenantId: string;
    clientId: string;
    clientSecret?: string;
    samlMetadataUrl?: string;
  }): Promise<{ success: boolean; message: string }> {
    // Convert camelCase to snake_case for backend
    const payload = {
      tenant_id: config.tenantId,
      client_id: config.clientId,
      client_secret: config.clientSecret,
      saml_metadata_url: config.samlMetadataUrl,
    };
    const response = await this.client.put('/admin/sso/microsoft-entra', payload);
    return response.data;
  }

  async configureGoogleWorkspace(config: {
    clientId?: string;
    clientSecret?: string;
    domain: string;
    samlMetadataUrl?: string;
    samlMetadataXml?: string;
    serviceAccountJson?: string;
    adminEmail?: string;
  }): Promise<{ success: boolean; message: string }> {
    // Convert camelCase to snake_case for backend
    const payload = {
      domain: config.domain,
      saml_metadata_url: config.samlMetadataUrl,
      saml_metadata_xml: config.samlMetadataXml,
      service_account_json: config.serviceAccountJson,
      admin_email: config.adminEmail,
    };
    const response = await this.client.put('/admin/sso/google-workspace', payload);
    return response.data;
  }

  async activateSso(provider: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/admin/sso/activate', { provider });
    return response.data;
  }

  async deactivateSso(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/admin/sso/deactivate');
    return response.data;
  }

  async fetchEntraGroups(config: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
  }): Promise<{ groups: Array<{ id: string; name: string; description: string | null }> }> {
    // Convert camelCase to snake_case for backend
    const payload = {
      tenant_id: config.tenantId,
      client_id: config.clientId,
      client_secret: config.clientSecret,
    };
    const response = await this.client.post('/admin/sso/microsoft-entra/groups', payload);
    return response.data;
  }

  async fetchGoogleGroups(config: {
    serviceAccountJson: string;
    domain: string;
    adminEmail: string;
  }): Promise<{ groups: Array<{ id: string; name: string; email?: string; description: string | null }> }> {
    // Convert camelCase to snake_case for backend
    const payload = {
      service_account_json: config.serviceAccountJson,
      domain: config.domain,
      admin_email: config.adminEmail,
    };
    const response = await this.client.post('/admin/sso/google-workspace/groups', payload);
    return response.data;
  }

  async getRoleMappings(provider: string): Promise<{
    admin_group_ids: string[];
    leadership_group_ids: string[];
    product_team_group_ids: string[];
    user_group_ids: string[];
  }> {
    const response = await this.client.get(`/admin/sso/${provider}/role-mappings`);
    return response.data;
  }

  async updateRoleMappings(
    provider: string,
    mappings: {
      admin_group_ids: string[];
      leadership_group_ids: string[];
      product_team_group_ids: string[];
      user_group_ids: string[];
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.client.put(`/admin/sso/${provider}/role-mappings`, mappings);
    return response.data;
  }
}

export const adminApiClient = new AdminApiClient();
